import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  value?: Date | string | null
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
}: DatePickerProps) {
  // Convert string to Date if needed
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    if (value instanceof Date) return value
    // Handle YYYY-MM-DD string format
    if (typeof value === 'string') {
      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? undefined : parsed
    }
    return undefined
  }, [value])

  const handleSelect = (date: Date | undefined) => {
    onChange?.(date)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateValue ? format(dateValue, "MMMM d, yyyy") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

/**
 * DatePickerField - For use with React Hook Form
 *
 * Example usage:
 *
 * <FormField
 *   control={form.control}
 *   name="birthday"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Birthday</FormLabel>
 *       <FormControl>
 *         <DatePickerField
 *           value={field.value}
 *           onChange={field.onChange}
 *         />
 *       </FormControl>
 *       <FormMessage />
 *     </FormItem>
 *   )}
 * />
 */
export interface DatePickerFieldProps {
  value?: Date | string | null
  onChange?: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function DatePickerField({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
}: DatePickerFieldProps) {
  // Convert string to Date for display
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    if (value instanceof Date) return value
    if (typeof value === 'string' && value) {
      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? undefined : parsed
    }
    return undefined
  }, [value])

  const handleSelect = (date: Date | undefined) => {
    // Convert Date to YYYY-MM-DD string for form storage
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      onChange?.(`${year}-${month}-${day}`)
    } else {
      onChange?.(null)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateValue ? format(dateValue, "MMMM d, yyyy") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

/**
 * DateRangePicker - For selecting date ranges
 *
 * Example usage:
 *
 * <DateRangePicker
 *   from={startDate}
 *   to={endDate}
 *   onSelect={(range) => {
 *     setStartDate(range?.from)
 *     setEndDate(range?.to)
 *   }}
 * />
 */
export interface DateRangePickerProps {
  from?: Date | string | null
  to?: Date | string | null
  onSelect?: (range: { from: Date | undefined; to: Date | undefined } | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function DateRangePicker({
  from,
  to,
  onSelect,
  placeholder = "Pick a date range",
  disabled = false,
  className,
}: DateRangePickerProps) {
  // Convert strings to Dates
  const fromDate = React.useMemo(() => {
    if (!from) return undefined
    if (from instanceof Date) return from
    if (typeof from === 'string') {
      const parsed = new Date(from)
      return isNaN(parsed.getTime()) ? undefined : parsed
    }
    return undefined
  }, [from])

  const toDate = React.useMemo(() => {
    if (!to) return undefined
    if (to instanceof Date) return to
    if (typeof to === 'string') {
      const parsed = new Date(to)
      return isNaN(parsed.getTime()) ? undefined : parsed
    }
    return undefined
  }, [to])

  const handleSelect = (range: any) => {
    onSelect?.(range)
  }

  const displayText = React.useMemo(() => {
    if (fromDate && toDate) {
      return `${format(fromDate, "MMMM d, yyyy")} - ${format(toDate, "MMMM d, yyyy")}`
    }
    if (fromDate) {
      return format(fromDate, "MMMM d, yyyy")
    }
    return placeholder
  }, [fromDate, toDate, placeholder])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !fromDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span>{displayText}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={{ from: fromDate, to: toDate }}
          onSelect={handleSelect}
          numberOfMonths={2}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
